/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectorRef, Component, effect, EventEmitter, Input, OnInit, Output, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AbcCore } from '@barthauer/abc/core';
import { PasswordRequirementsInfo, UserService } from '@barthauer/abc/core/authentication';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { _AbcFormComponent } from '@barthauer/abc/ui-components';
import { TextBoxPredefinedButton, TextEditorButton } from 'devextreme/common';
import { FieldDataChangedEvent } from 'devextreme/ui/form';
import { firstValueFrom, Observable, of, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { UserViewModel } from './models/view.model';
import { DataService, PersistData } from './services/data.service';
import { PasswordValidationService } from './services/validation.service';
import { ContextService } from './services/context.service';
import { MUser } from './models/data.model';

@Component({
  selector: 'abc-management-users-user-settings',
  templateUrl: './user-settings.component.html',
  styleUrls: ['./user-settings.component.scss'],
  standalone: false,
})
export class UserSettingsComponent implements OnInit {
  public abcForm = viewChild<_AbcFormComponent>(_AbcFormComponent);

  @Input() allowUserNameChange = true;
  @Input() allowPasswordChange = true;
  @Input() useLocalPasswordChangeModel = false;
  @Input() passwordResetFunc: (newPassword: string) => Promise<boolean>;
  @Input() handlePasswordChangeSuccess: () => void | Promise<void>;
  @Input() handlePasswordChangeError: () => void | Promise<void>;
  @Input() newUserGroupName = 'BS_KAOS_Customer';
  @Input() clearIcon: string;
  @Input() showPasswordIcon: string;
  @Input() hidePasswordIcon: string;
  @Input() showActionButtons = true;

  @Output() readonly changedEvent = new EventEmitter<void>();
  @Output() onPasswordChanged: EventEmitter<void> = new EventEmitter();

  private saveData$ = new Subject<PersistData>();
  public refreshData$ = new Subject<void>();
  public res: ResourceSet = ResourceSet.Empty;
  persistData$: Observable<void> = of(undefined);
  queryData$: Observable<void> = of(undefined);
  errorMessage = '';
  isSaving = false;
  isLoading = false;
  isPasswordChanging = false;
  passwordRequirementsWarnings: string[] = [];
  passwordRequirements: PasswordRequirementsInfo | undefined;

  user?: UserViewModel;

  get isPending(): boolean {
    return this.isLoading || this.isSaving || this.isPasswordChanging;
  }

  get isUserChanged(): boolean {
    return this.user !== undefined && this.user.hasChanges;
  }

  get isPasswordChanged(): boolean {
    return this.user !== undefined && this.user.password.hasChanges;
  }

  get isUserValid(): boolean {
    return this.user !== undefined && this.user.isValid;
  }

  private _isCurrentPasswordValid = false;
  private _isNewPasswordValid = false;
  private _isConfirmPasswordValid = false;

  get isCurrentPasswordValid(): boolean {
    return !!this.passwordResetFunc || this._isCurrentPasswordValid;
  }

  get isNewPasswordValid(): boolean {
    return this._isNewPasswordValid;
  }

  get isConfirmPasswordValid(): boolean {
    return this._isConfirmPasswordValid;
  }

  get isPasswordValid(): boolean {
    return (
      this.isCurrentPasswordValid &&
      this._isNewPasswordValid &&
      this._isConfirmPasswordValid &&
      this.user?.password.newPassword === this.user?.password.confirmPassword
    );
  }

  get isSaveDisabled(): boolean {
    return this.isPending || !this.isPasswordValid || this.isUserChanged;
  }

  public currentPasswordTextBoxButton: string | TextEditorButton[] = [];
  public newPasswordTextBoxButton: string | TextEditorButton[] = [];
  public confirmPasswordTextBoxButton: string | TextEditorButton[] = [];
  constructor(
    public _res: ResourceManagerService,
    private dataService: DataService,
    private router: Router,
    public coreService: AbcCore,
    private messageBoxService: MessageBoxService,
    private userService: UserService,
    private passwordValidationService: PasswordValidationService,
    private contextService: ContextService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    effect(async () => {
      if (this.abcForm()) {
        if (!this.passwordResetFunc) {
          this.currentPasswordTextBoxButton = await this.createDxTextBoxButton('password.currentPassword');
        }
        this.confirmPasswordTextBoxButton = await this.createDxTextBoxButton('password.confirmPassword');
        this.newPasswordTextBoxButton = await this.createDxTextBoxButton('password.newPassword');

        const inputs = this.abcForm().instance.element().querySelectorAll('input');
        inputs?.forEach((input: HTMLInputElement) => {
          input.addEventListener('blur', this.onTextInputBlur);
        });
      }
    });
  }

  async ngOnInit(): Promise<void> {
    if (this.passwordResetFunc || this.useLocalPasswordChangeModel) {
      this.user = new UserViewModel(this.contextService, new MUser({}));
      this.queryData$ = of(undefined);
      this.persistData$ = of(undefined);
    } else {
      this.queryData$ = this.dataService.queryUser(this, this.refreshData$);
      this.persistData$ = this.dataService.persistData(this, this.saveData$, this.refreshData$);
    }

    this.res = await this._res.getResourceSet('abc-management-users');

    if (this.allowPasswordChange) {
      await this.readPasswordRequirements();
    }

    this.changeDetectorRef.detectChanges();
  }

  readonly saveUser = () => this.saveUserCore(() => this.changedEvent.emit());
  private saveUserCore(continueWith?: () => void, noRefresh?: boolean): void {
    const persistData = new PersistData(() => this.dataService.persistUser(this.user!));
    persistData.continueWith = continueWith;
    persistData.noRefresh = noRefresh;
    this.saveData$.next(persistData);
  }

  readonly changePassword = async () => {
    if (!!this.passwordResetFunc) {
      await this.passwordResetFunc(this.user!.password.newPassword);
    } else {
      await this.changePasswordCore(
        this.user!.password.currentPassword,
        this.user!.password.newPassword,
        this.user!.password.confirmPassword,
      );
    }
  };

  public async changePasswordCore(oldPassword: string, newPassword: string, confirmPassword: string): Promise<void> {
    this.isPasswordChanging = true;
    try {
      await firstValueFrom(this.userService.ChangeUserPassword(oldPassword, newPassword, confirmPassword));

      if (this.handlePasswordChangeSuccess) {
        this.handlePasswordChangeSuccess();
        this._isNewPasswordValid = false;
      } else {
        alert(this.res.get('UserSettings-PasswordChanged'));
      }

      this.changedEvent.emit();
      this.refreshData$.next();
      this.onPasswordChanged.emit();
    } catch (error) {
      if (this.handlePasswordChangeError) {
        this.handlePasswordChangeError();
      } else {
        this.coreService.error.publish(error);
      }
    } finally {
      this.isPasswordChanging = false;
    }
  }

  private navigate(url: string): void {
    if (this.isUserChanged) {
      const settings = new MessageSettings();
      settings.caption = this.res.get('UserSettings-UserChanged');

      if (this.isUserValid) {
        settings.mode = MessageMode.Question;
        settings.button = MessageBoxButtons.YesNoCancel;
        settings.message = this.res.get('UserSettings-SaveChanges');
        this.showMessageBox(settings, (dialogResult) => {
          if (dialogResult === DialogResult.Yes) {
            this.saveUserCore(() => this.router.navigate([url]), true);
          } else if (dialogResult === DialogResult.No) {
            this.router.navigate([url]);
          }
        });
      } else {
        settings.mode = MessageMode.Info;
        settings.button = MessageBoxButtons.OKCancel;
        settings.message = this.res.get('UserSettings-DiscardChanges');
        this.showMessageBox(settings, (dialogResult) => {
          if (dialogResult === DialogResult.OK) {
            this.router.navigate([url]);
          }
        });
      }
    } else {
      this.router.navigate([url]);
    }
  }

  private showMessageBox(settings: MessageSettings, handleResult: (dialogResult: DialogResult) => void): void {
    this.messageBoxService
      .showMessageBox(settings)
      .pipe(take(1))
      .subscribe((dialogResult) => handleResult(dialogResult));
  }

  protected async readPasswordRequirements(): Promise<void> {
    if (this.passwordRequirementsWarnings.length) {
      return;
    }
    try {
      if (!!this.passwordResetFunc) {
        const newUserInfo = await firstValueFrom(this.userService.queryNewUserInfo([this.newUserGroupName]));
        this.passwordRequirements = newUserInfo?.passwordRequirements;
      } else {
        const loginInfo = await firstValueFrom(this.userService.getCurrentLoginInfo());
        this.passwordRequirements = loginInfo?.passwordRequirements;
      }
    } catch {
      console.error('Failed to fetch password requirements');
    }
    if (this.passwordRequirements) {
      this.passwordRequirementsWarnings = await this.passwordValidationService.createPasswordRequirementsWarnings(
        this.passwordRequirements,
      );
    }
    if (!this.passwordResetFunc) {
      this.passwordRequirementsWarnings.push(this.res.get('UserSettings-PasswordDifference'));
    }
  }

  onFieldDataChanged(e: FieldDataChangedEvent) {
    if (
      e.dataField === 'password.currentPassword' ||
      e.dataField === 'password.newPassword' ||
      e.dataField === 'password.confirmPassword'
    ) {
      this.updatePasswordValidFlag();
      this.abcForm()?.instance.validate();
    }

    if (e.dataField === 'name' || e.dataField === 'email' || e.dataField === 'login') {
      this.changedEvent.emit();
    }
  }

  private updatePasswordValidFlag() {
    if (this.user) {
      this._isCurrentPasswordValid =
        !!this.user.password.currentPassword.length &&
        this.user.password.currentPassword.trim() !== this.user.password.newPassword.trim();

      this._isNewPasswordValid =
        !!this.user.password.newPassword.length &&
        !!this.passwordValidationService
          .validatePassword(this.user.password.newPassword, this.passwordRequirements)
          .isValid() &&
        (!!this.passwordResetFunc ||
          this.user.password.newPassword.trim() !== this.user.password.currentPassword.trim());

      this._isConfirmPasswordValid =
        !!this.user.password.confirmPassword.length &&
        !!this.passwordValidationService
          .validatePassword(this.user.password.confirmPassword, this.passwordRequirements)
          .isValid() &&
        (!!this.passwordResetFunc ||
          this.user.password.confirmPassword.trim() !== this.user.password.currentPassword.trim());
    }
  }

  public cancelPasswordChange = () => {
    if (this.passwordResetFunc) {
      this.router.navigate(['/login']);
    } else {
      window.location.reload();
    }
  };
  public getAuthenticationCaption(): string | undefined {
    if (this.allowUserNameChange && this.showActionButtons) {
      return this.res.get('UserSettings-Authentication');
    } else if (this.allowUserNameChange && !this.showActionButtons) {
      return this.res.get('UserSettings-ChangePassword');
    }
    return undefined;
  }

  public getTextInputClass(): string {
    return 'textInput';
  }

  public getSubmitButtonClass(): string {
    return 'submitButton';
  }

  public getGroupAuthenticationClass(): string {
    if (this.allowUserNameChange && this.showActionButtons) {
      return '';
    } else if (this.allowUserNameChange && !this.showActionButtons) {
      return 'changePasswordGroup';
    }
  }

  public getSubmitIconClass(): string {
    return 'submitIcon';
  }

  public getCancelButtonClass(): string {
    return 'cancelButton';
  }

  protected onTextInputBlur = (e: FocusEvent): void => {
    const inputTarget = e.target as HTMLInputElement;
    const TrimmedValue = inputTarget.value.trim();
    const key = inputTarget.name.split('.')[1];

    this.user.password[key] = TrimmedValue;
    inputTarget.value = TrimmedValue;
  };

  private async createDxTextBoxButton(
    dataField: string,
    mode: 'password' | 'text' = 'password',
  ): Promise<string | TextBoxPredefinedButton | TextEditorButton[]> {
    const visibilityIcon =
      this.showPasswordIcon && this.hidePasswordIcon
        ? mode === 'password'
          ? this.showPasswordIcon
          : this.hidePasswordIcon
        : await this.res.svgContent(mode === 'password' ? 'password-hide-image' : 'password-show-image');

    const clearIcon = this.clearIcon ?? (await this.res.svgContent('input-clear-image'));

    return [
      {
        name: dataField + '_toggle',
        location: 'after',
        options: {
          icon: visibilityIcon,
          type: 'default',
          tabIndex: -1,
          onClick: async () => {
            const editor = this.abcForm()!.instance.getEditor(dataField);

            if (!editor) {
              console.warn('Editor ' + dataField + 'not found');
              return;
            }

            const currentMode = editor.option('mode');
            const newMode = currentMode === 'password' ? 'text' : 'password';

            editor.option('mode', newMode);
            editor.option('buttons', await this.createDxTextBoxButton(dataField, newMode));
          },
        },
      },
      {
        name: dataField + '_clear',
        location: 'after',
        options: {
          icon: clearIcon,
          type: 'default',
          tabIndex: -1,
          onClick: async () => {
            const editor = this.abcForm()!.instance.getEditor(dataField);

            if (!editor) {
              console.warn('Editor ' + dataField + 'not found');
              return;
            }

            editor.option('value', '');
          },
        },
      },
    ];
  }
}
